import styled from '@emotion/styled';

import { media } from '@myrealtrip/modash';
import { Text } from '@myrealtrip/web-ui';

import Show from '~components/Show';
import { PASS_SECTION_TITLE, PASS_CARD_SECTION_DIVIDER_HEIGHT } from '~constants/pass';
import { useDetermineMobile } from '@myrealtrip/device-detect';

import type { TransportationProduct } from '~models/Transportation/PointToPointTicket';
import type { PassSectionKey } from '~types/pass';

import PassCard from './PassCard';

interface Props {
  sectionKey: PassSectionKey;
  subscribe: (key: PassSectionKey) => (node: HTMLElement | null) => void;
  products: TransportationProduct[] | [];
  sectionVerticalIndex: number;
  isLast: boolean;
}

const PassSection = ({ sectionKey, subscribe, products, sectionVerticalIndex, isLast }: Props) => {
  const isMobile = useDetermineMobile();

  if (products.length === 0) {
    return null;
  }

  return (
    <>
      <Section ref={subscribe(sectionKey)} isLast={isLast}>
        <SectionTitle as="h2" typography="headlineBold24">
          {PASS_SECTION_TITLE[sectionKey]}
        </SectionTitle>
        <CardList>
          {products.map((product, index) => (
            <PassCard
              key={product.gid}
              product={product}
              sectionTitle={PASS_SECTION_TITLE[sectionKey]}
              sectionVerticalIndex={sectionVerticalIndex}
              itemIndex={index}
            />
          ))}
        </CardList>
      </Section>
      <Show when={isMobile && !isLast}>
        <Divider />
      </Show>
    </>
  );
};

export default PassSection;

const Section = styled.div<{ isLast: boolean }>`
  padding: 20px 0 ${({ isLast }) => (isLast ? '172px' : '32px')};

  ${media.minMobile} {
    padding: 20px 0 32px;
    &:not(:last-child) {
      margin: 0 0 12px;
    }
  }
`;

const SectionTitle = styled(Text)`
  margin: 0 0 20px;
`;

const CardList = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  row-gap: 20px;

  ${media.minMobile} {
    grid-template-columns: repeat(2, 1fr);
    column-gap: 20px;
    row-gap: 24px;
  }
`;

const Divider = styled.div`
  width: calc(100% + 40px);
  height: ${PASS_CARD_SECTION_DIVIDER_HEIGHT}px;
  background-color: #f5f5f5;
  margin-left: -20px;
`;
